import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import {
  DetailAgreementRef,
  EnrichmentDetail,
} from "@app/shared/models/enrichment-detail.model";
import {
  ActiveLanguage,
  LanguageConfigStore,
} from "@app/shared/services/language-config.store";
import { LanguageModeService } from "@app/shared/services/language-mode.service";
import { logger } from "@core/utils/logger";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import {
  FlatEnrichmentModel,
  isFlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  extractMultilingual,
  SUPPORTED_LOCALES,
  SupportedLocale,
} from "@app/shared/utils/multilingual.util";
import { IAgreement, LocaleString } from "@app/shared/models/agreement.model";
import { AgreementService } from "@app/shared/services/agreement.service";
import { AgreementContentStore } from "../../../services/agreement-content.store";
import {
  AGREEMENT_STATUS_ROW,
  AgreementSearch,
} from "../../../services/agreement-search";
import { EnrichmentDataService } from "../../../services/enrichment-data.service";
import { ActivePriceStore } from "../enrichment-edit-package/active-price.store";
import { SectionDirtyStore } from "../enrichment-edit-package/section-dirty.store";
import { PACKAGE_SETTINGS_TAB } from "../enrichment-edit-package/tab-algebra.util";
import { TermRichEditor } from "./term-rich-editor/term-rich-editor";
import { sanitizeTermHtml } from "@shared/components/term-rich-editor/word-paste.util";

/** A T&C template — projected from a TMF `Agreement`.
 *
 *  - `name` is the chooser-friendly fallback label (EN → TH → id).
 *  - `nameByLocale` carries the full `name` LocaleString from the
 *    Agreement, so the create form can round-trip per-locale names.
 *  - `content` is per-locale HTML body sourced from the Agreement's
 *    `description` LocaleString (the AIS BE uses `description` as the
 *    clause body; `agreementItem[]` is unused).
 *  - `agreementType`, `status`, `initialDate`, `specName`, `specId`
 *    expose the BE metadata visible on the response so the preview
 *    pane can label them. */
export interface TermTemplate {
  id: string;
  name: string;
  nameByLocale?: Record<string, string>;
  content: Record<string, string>;
  /** Where its `/v1/agreement/{id}` detail stands; absent until asked for.
   *  Lives on the entry, so a replaced list starts over. */
  detail?: "loading" | "loaded" | "failed";
  agreementType?: string;
  status?: string;
  initialDate?: string;
  specName?: string;
  specId?: string;
}

/**
 * Term tab — Terms & Conditions template picker + preview + create.
 *
 * Three zones inside the section card:
 *   1. **Selector** — dropdown of available T&C templates + "Create new".
 *   2. **Preview** — locale tabs over the selected template's HTML
 *      content (rendered via `[innerHTML]` + `DomSanitizer` so the
 *      template's tags survive).
 *   3. **Create form** — inline panel (toggled from the header) with a
 *      name input and a per-locale rich text editor. Saving appends a
 *      new template to the list and auto-selects it.
 *
 * Storage on the enrichment model: the selected template's id rides on
 * `__detail.agreements[0]` as a flat `{id, name}` ref (single-select
 * UI — index 0 only). The template content itself lives in the template
 * list (FE seed + BE-fetched); it isn't duplicated onto every offering.
 */
@Component({
  selector: "app-enrichment-term",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    OmniarkUiModule,
    TermRichEditor,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-term.html",
  styleUrls: ["./enrichment-term.scss"],
})
export class EnrichmentTerm {
  private readonly data = inject(EnrichmentDataService);
  private readonly active = inject(ActivePriceStore);
  /** Section-dirty attribution — which TAB the discard snapshot should
   *  blame. Optional: absent when the tab is mounted standalone in specs. */
  private readonly sectionDirty = inject(SectionDirtyStore, {
    optional: true,
  });
  private readonly languageConfig = inject(LanguageConfigStore);
  private readonly languageMode = inject(LanguageModeService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly agreementService = inject(AgreementService);
  private readonly agreementContent = inject(AgreementContentStore);
  private readonly destroyRef = inject(DestroyRef);

  /** The picker's server-side search; idle until it is hovered or opened. */
  protected readonly search = new AgreementSearch(this.agreementContent);

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  protected readonly detail = computed<EnrichmentDetail | null>(() => {
    const m = this.model();
    return m && isFlatEnrichmentModel(m) ? m.__detail : null;
  });

  /** Prices that carry their own Agreement (Pricing → Agreement) and so do
   *  NOT follow the one picked here — CONTEXT.md "Price Agreement". */
  protected readonly priceAgreementOverrides = computed(() => {
    const prices = this.detail()?.prices ?? [];
    const own = prices.filter((p) => p.agreements?.some((a) => !!a?.id));
    return {
      own: own.length,
      total: prices.length,
      firstId: own[0]?.id ?? null,
    };
  });

  protected reviewPriceAgreements(): void {
    const id = this.priceAgreementOverrides().firstId;
    if (id == null) return;
    void this.active.goTo(id).then((switched) => {
      if (!switched) return;
      this.data.requestEditorAction({
        tabId: "price-config",
        sectionId: "section-agreement",
      });
    });
  }

  /** Active editing locales — drives the preview tabs + create form
   *  language rows (only renders editors for locales the user has
   *  enabled in the hero language config). */
  protected readonly languages = computed<ActiveLanguage[]>(() =>
    this.languageConfig.active(),
  );

  /** Language set actually shown in the edit/create stack. Respects the
   *  hero-wide language view filter (`LanguageModeService`): when the user
   *  clicks an "EN" / "TH" hero card the editor collapses to that single
   *  language only — clicking "ALL" (or no selection) restores the full
   *  `languages()` set. Mirrors `pricing-locale-input.visibleLanguages`. */
  protected readonly visibleLanguages = computed<ActiveLanguage[]>(() => {
    const all = this.languages();
    const view = this.languageMode.active();
    if (view === "ALL") return all;
    const target = String(view).toUpperCase();
    const filtered = all.filter(
      (l) => (l.shortCode || l.label || "").toUpperCase() === target,
    );
    // Unknown view code → don't silently render an empty editor; fall
    // back to the full active set so the user still has something to edit.
    return filtered.length ? filtered : all;
  });

  /** Sectional numbering — keeps lockstep with the numbering chips
   *  used in Pricing / Shelf. Only one section here, so always 1. */
  protected readonly sectionNumber = 1;

  /* ── Template list ─────────────────────────────────────────────── */

  protected readonly templates = signal<TermTemplate[]>([]);

  /** SelectOption[] adapter for `<enr-select>`: the search hits, the
   *  selected template, and a status row from {@link AgreementSearch}. */
  protected readonly templateOptions = computed<SelectOption[]>(() => {
    // Resolve each option's label in the SAME locale the preview renders so the
    // picker and preview never disagree — was always the EN-first `t.name`,
    // which read as English while the preview showed Thai. Falls back to
    // `t.name` when the template has no name in that locale. (#72)
    const loc = this.previewLocale();
    const ids = this.search.hits().map((a) => a.id);
    const selected = this.selectedTemplateId();
    if (selected && !ids.includes(selected)) ids.push(selected);
    const byId = new Map(this.templates().map((t) => [t.id, t]));
    const status = this.search.statusRow();
    return [
      { value: "", label: "— Choose a template —" },
      ...ids
        .map((id) => byId.get(id))
        .filter((t): t is TermTemplate => !!t)
        .map((t) => ({
          value: t.id,
          label: this.search.label(
            t.nameByLocale,
            t.nameByLocale?.[loc]?.trim() || t.name,
          ),
        })),
      ...(status ? [status] : []),
    ];
  });

  /** Selected template id — driven by the dropdown + write-back. The
   *  FormControl is the wire to `<enr-select>` (which requires
   *  `formControl` binding); we mirror its value into `selectedTemplateId`
   *  so the rest of the component reads via signals as usual. */
  protected readonly templateControl = new FormControl<string>("", {
    nonNullable: true,
  });
  protected readonly selectedTemplateId = signal<string>("");

  protected readonly selectedTemplate = computed<TermTemplate | null>(() => {
    const id = this.selectedTemplateId();
    if (!id) return null;
    return this.templates().find((t) => t.id === id) ?? null;
  });

  /** The selected agreement's detail is still on its way: the preview
   *  shows a skeleton, not "Not authored in this language yet". Read off
   *  the entry, so an agreement already fetched shows at once. */
  protected readonly loadingPreview = computed<boolean>(
    () => this.selectedTemplate()?.detail === "loading",
  );

  /** The selected agreement's detail could not be fetched (GET answered an
   *  error, e.g. 404 for an id held only as a channel copy). The entry is a
   *  stub with no name or content to edit, and a PATCH would go to an
   *  agreement the API cannot update, so the preview says so and Edit stays
   *  closed. */
  protected readonly previewFailed = computed<boolean>(
    () => this.selectedTemplate()?.detail === "failed",
  );

  /** Edit opens only on an agreement whose detail is on screen. */
  protected readonly canStartEdit = computed<boolean>(
    () =>
      !!this.selectedTemplate() &&
      !this.loadingPreview() &&
      !this.previewFailed(),
  );

  /* ── Preview locale tabs ──────────────────────────────────────── */

  /** Manual preview-tab pick, or null to follow the active default. */
  private readonly previewLocaleOverride = signal<string | null>(null);

  /** Active locale tab in the preview pane. Defaults to the locale the hero
   *  language view targets (`LanguageModeService.activeLocale` — the language
   *  the user has selected, EN-first when "ALL"); the dropdown option labels
   *  resolve in this same locale so picker and preview never disagree (#72).
   *  This also means a freshly-saved template opens on the language the user
   *  just authored in, rather than a fixed default that reads "not authored in
   *  this language yet" (#73). A manual tab pick wins while it stays in the
   *  active set; otherwise it falls back to the hero locale. */
  protected readonly previewLocale = computed<string>(() => {
    const override = this.previewLocaleOverride();
    if (override && this.languages().some((l) => l.locale === override)) {
      return override;
    }
    return this.languageMode.activeLocale();
  });

  /** The selected template's localized display name for the active
   *  preview locale. Falls back to the dropdown label (EN→TH→id) when
   *  the template has no name in the current locale. */
  protected readonly previewName = computed<string>(() => {
    const tpl = this.selectedTemplate();
    if (!tpl) return "";
    const localized = tpl.nameByLocale?.[this.previewLocale()];
    if (localized && localized.trim().length > 0) return localized;
    return tpl.name;
  });

  protected readonly previewHtml = computed<SafeHtml | null>(() => {
    const tpl = this.selectedTemplate();
    if (!tpl) return null;
    const html = tpl.content[this.previewLocale()];
    if (!html || !html.trim()) return null;
    // The body is an agreement's stored description — re-served by the BE,
    // so untrusted (pentest pre-check 2026-10-26: a crafted description ran
    // script here). Narrow it to the editor's own DOMPurify allow-list first,
    // exactly as the mobile preview does, then bypass so Angular keeps the
    // <ul>/<strong>/table markup that list allows.
    const safe = sanitizeTermHtml(html);
    return safe ? this.sanitizer.bypassSecurityTrustHtml(safe) : null;
  });

  /** True when the preview locale has no content for the selected
   *  template — drives the muted "Not authored in this language yet"
   *  fallback in the preview body. */
  protected readonly previewIsEmpty = computed<boolean>(() => {
    const tpl = this.selectedTemplate();
    if (!tpl) return true;
    const html = tpl.content[this.previewLocale()];
    return !html || !html.trim();
  });

  setPreviewLocale(locale: string): void {
    this.previewLocaleOverride.set(locale);
  }

  /* ── Create form ──────────────────────────────────────────────── */

  protected readonly creating = signal<boolean>(false);

  /** Draft template the user is authoring — mirrors the TMF Agreement
   *  shape:
   *   - `draftName` is a LocaleString (per-locale display name).
   *   - `draftContent` is per-locale `description` (the body shown in
   *     the preview pane).
   *   - `draftType` / `draftStatus` are the matching string fields on
   *     the response (defaults `package` / `active`).
   *  The save path remains FE-only for now (no POST); when the BE
   *  create endpoint is wired this state maps 1:1 to the request body. */
  protected readonly draftName = signal<Record<string, string>>({});
  protected readonly draftContent = signal<Record<string, string>>({});
  protected readonly draftType = signal<string>("package");
  protected readonly draftStatus = signal<string>("active");

  /** A name exists in at least one language. */
  protected readonly draftHasName = computed<boolean>(() =>
    Object.values(this.draftName()).some((v) => v && v.trim().length > 0),
  );
  /** A description exists in at least one language. */
  protected readonly draftHasContent = computed<boolean>(() =>
    Object.values(this.draftContent()).some((v) => v && v.trim().length > 0),
  );
  /** Save requires a name AND a description, each in at least one language.
   *  Drives both the disabled state and the inline required-field message so
   *  an empty form is never silently un-saveable. (#70) */
  protected readonly canSaveDraft = computed<boolean>(
    () => this.draftHasName() && this.draftHasContent(),
  );

  openCreate(): void {
    this.creating.set(true);
    this.draftName.set({});
    this.draftContent.set({});
    this.draftType.set("package");
    this.draftStatus.set("active");
  }

  cancelCreate(): void {
    this.creating.set(false);
    this.draftName.set({});
    this.draftContent.set({});
  }

  onDraftNameInput(locale: string, event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.draftName.update((c) => ({ ...c, [locale]: v }));
  }

  draftNameFor(locale: string): string {
    return this.draftName()[locale] ?? "";
  }

  onDraftContentChange(locale: string, html: string): void {
    this.draftContent.update((c) => ({ ...c, [locale]: html }));
  }

  /** Read the current draft content for a locale — feeds the rich
   *  editor's `value` input on render. */
  draftContentFor(locale: string): string {
    return this.draftContent()[locale] ?? "";
  }

  onDraftTypeInput(event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.draftType.set(v);
  }

  onDraftStatusInput(event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.draftStatus.set(v);
  }

  saveDraft(): void {
    if (!this.canSaveDraft() || this.saving()) return;
    const body: Partial<IAgreement> = {
      name: this.toLocaleStringPayload(this.draftName()),
      description: this.toLocaleStringPayload(this.draftContent()),
      agreementType: this.draftType().trim() || undefined,
      status: this.draftStatus().trim() || undefined,
    };
    this.saving.set(true);
    this.agreementService
      .createAgreement(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.agreementContent.put(created);
          // The BE assigns the id; project the response into a template the
          // same way the list/edit paths do so the preview pane labels match.
          const tpl: TermTemplate = {
            ...this.toShellTemplate(created),
            content: this.buildContent(created),
            detail: "loaded",
          };
          this.templates.update((list) => [...list, tpl]);
          this.show(tpl.id);
          this.writeSelectionBack(tpl.id);
          this.saving.set(false);
          this.creating.set(false);
          // Open the new template's preview on the selected (hero) language, not
          // a stale tab from a previously-previewed template. (#73)
          this.previewLocaleOverride.set(null);
          this.draftName.set({});
          this.draftContent.set({});
        },
        error: (error) => {
          logger.error("Failed to create agreement:", error);
          this.saving.set(false);
        },
      });
  }

  /* ── Edit (modify existing agreement) ────────────────────────────
     Inline editor that swaps in over the preview pane when the user
     clicks "Edit". Drafts mirror the same fields as create; Save
     posts a PATCH back to `/v1/agreement/{id}` and refreshes the
     local template cache from the BE response. */

  protected readonly editing = signal<boolean>(false);
  protected readonly saving = signal<boolean>(false);

  protected readonly editName = signal<Record<string, string>>({});
  protected readonly editContent = signal<Record<string, string>>({});
  protected readonly editType = signal<string>("");
  protected readonly editStatus = signal<string>("");

  protected readonly canSaveEdit = computed<boolean>(() => {
    // Require at least one locale of `name` so the agreement stays
    // identifiable. Description can be cleared per locale on purpose
    // (e.g. removing a stale translation).
    return Object.values(this.editName()).some((v) => v && v.trim().length > 0);
  });

  startEdit(): void {
    const tpl = this.selectedTemplate();
    if (!tpl || !this.canStartEdit()) return;
    this.editName.set({ ...(tpl.nameByLocale ?? {}) });
    this.editContent.set({ ...tpl.content });
    this.editType.set(tpl.agreementType ?? "");
    this.editStatus.set(tpl.status ?? "");
    this.editing.set(true);
  }

  cancelEdit(): void {
    this.editing.set(false);
    this.editName.set({});
    this.editContent.set({});
    this.editType.set("");
    this.editStatus.set("");
  }

  editNameFor(locale: string): string {
    return this.editName()[locale] ?? "";
  }

  editContentFor(locale: string): string {
    return this.editContent()[locale] ?? "";
  }

  onEditNameInput(locale: string, event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.editName.update((c) => ({ ...c, [locale]: v }));
  }

  onEditContentChange(locale: string, html: string): void {
    this.editContent.update((c) => ({ ...c, [locale]: html }));
  }

  onEditTypeInput(event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.editType.set(v);
  }

  onEditStatusInput(event: Event): void {
    const v = (event.target as HTMLInputElement | null)?.value ?? "";
    this.editStatus.set(v);
  }

  saveEdit(): void {
    const tpl = this.selectedTemplate();
    if (!tpl || !this.editing() || !this.canSaveEdit() || this.saving()) return;
    const body: Partial<IAgreement> = {
      name: this.toLocaleStringPayload(this.editName()),
      description: this.toLocaleStringPayload(this.editContent()),
      agreementType: this.editType().trim() || undefined,
      status: this.editStatus().trim() || undefined,
    };
    this.saving.set(true);
    this.agreementService
      .patchAgreement(tpl.id, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.agreementContent.put(updated);
          this.updateEntry(tpl.id, () => ({
            ...this.toShellTemplate(updated),
            content: this.buildContent(updated),
            detail: "loaded",
          }));
          this.saving.set(false);
          this.cancelEdit();
        },
        error: (error) => {
          logger.error(`Failed to update agreement ${tpl.id}:`, error);
          this.saving.set(false);
        },
      });
  }

  /** Filter a draft bag down to non-empty values keyed by the four
   *  `SupportedLocale` codes. Empty strings collapse to absent keys so
   *  the PATCH body doesn't blank out a locale unintentionally — to
   *  truly remove a locale's value send `null`, which the BE treats as
   *  "clear this field". (We don't surface that distinction in the UI
   *  yet; clearing a locale's input simply omits it from the payload.) */
  private toLocaleStringPayload(bag: Record<string, string>): LocaleString {
    const out: LocaleString = {};
    for (const locale of SUPPORTED_LOCALES) {
      const value = bag[locale];
      if (typeof value === "string" && value.trim().length > 0) {
        out[locale] = value;
      }
    }
    return out;
  }

  trackTemplateId = (_: number, t: TermTemplate): string => t.id;
  trackLangLocale = (_: number, l: ActiveLanguage): string => l.locale;

  /* ── Write-back ──────────────────────────────────────────────────
     Persist the selected template as a flat agreement ref on
     `__detail.agreements[0]`. Single-select — clearing the picker
     drops the array entirely so the differential PATCH unbinds the
     agreement on the BE side. */

  private writeSelectionBack(templateId: string): void {
    const m = this.model();
    if (!m || !isFlatEnrichmentModel(m)) return;
    const next = structuredClone(m) as FlatEnrichmentModel;
    if (!templateId) {
      next.__detail.agreements = [];
    } else {
      const tpl = this.templates().find((t) => t.id === templateId);
      const ref: DetailAgreementRef = {
        id: templateId,
        name: tpl?.name,
      };
      next.__detail.agreements = [ref];
    }
    this.data.setEnrichment(next);
    this.sectionDirty?.markTabDirty(PACKAGE_SETTINGS_TAB);
  }

  /* ── Initial load + selection from the model ──────────────────── */

  constructor() {
    // Bridge FormControl → signal. `<enr-select>` writes into the
    // FormControl; we forward to `selectedTemplateId` (and trigger
    // the model write-back). `takeUntilDestroyed` cleans up on
    // navigation away. The hydration path uses `emitEvent: false` to
    // avoid re-entry.
    this.templateControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const id = value ?? "";
        if (id === AGREEMENT_STATUS_ROW) {
          this.templateControl.setValue(this.selectedTemplateId(), {
            emitEvent: false,
          });
          return;
        }
        if (id === this.selectedTemplateId()) return;
        this.show(id);
        this.writeSelectionBack(id);
      });

    // A Discard restores the snapshot's agreement without rebuilding this
    // pane, so the selection follows the model, not only the first load.
    this.data.enrichment$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.hydrateSelectionFromModel());

    // Search hits join the known templates without dropping a clause body
    // an entry has already loaded.
    toObservable(this.search.hits)
      .pipe(takeUntilDestroyed())
      .subscribe((hits) => {
        const known = new Map(this.templates().map((t) => [t.id, t]));
        for (const a of hits)
          known.set(a.id, {
            ...known.get(a.id),
            ...this.toShellTemplate(a),
            content: known.get(a.id)?.content ?? {},
          });
        this.templates.set([...known.values()]);
      });
  }

  /** Make the picker and preview show the model's agreement, again whenever
   *  the model changes under this pane. */
  private hydrateSelectionFromModel(): void {
    const ref = this.detail()?.agreements?.[0];
    const id = ref?.id ?? "";

    // The bound agreement need not be among the search hits: a stub keeps it
    // selectable until the detail fetch below fills in its name and content.
    if (id && !this.templates().some((t) => t.id === id)) {
      const stub: TermTemplate = { id, name: ref?.name || id, content: {} };
      this.templates.update((list) => [...list, stub]);
    }

    if (id !== this.selectedTemplateId()) this.show(id);
  }

  /** Put `id` on screen: the picker, the preview and its detail. An inline
   *  edit that was open belonged to the agreement before, so it is dropped:
   *  Save must never write one agreement's drafts onto another. */
  private show(id: string): void {
    this.cancelEdit();
    this.selectedTemplateId.set(id);
    // No emitEvent: the valueChanges handler would write the model back.
    this.templateControl.setValue(id, { emitEvent: false });
    if (id) this.loadAgreementDetail(id);
  }

  /** Fetch the full agreement (its per-locale `description`) into its
   *  entry. An entry whose detail landed, or is on its way, is left alone,
   *  so reselecting an agreement fetches nothing. */
  private loadAgreementDetail(id: string): void {
    const detail = this.templates().find((t) => t.id === id)?.detail;
    if (detail === "loaded" || detail === "loading") return;
    this.updateEntry(id, (entry) => ({ ...entry, detail: "loading" }));
    this.agreementContent
      .get(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((agreement) => {
        this.updateEntry(id, (entry) =>
          agreement
            ? {
                ...this.toShellTemplate(agreement),
                content: this.buildContent(agreement),
                detail: "loaded",
              }
            : { ...entry, detail: "failed" },
        );
      });
  }

  private updateEntry(
    id: string,
    update: (entry: TermTemplate) => TermTemplate,
  ): void {
    this.templates.update((list) =>
      list.map((t) => (t.id === id ? update(t) : t)),
    );
  }

  /** Project a TMF `Agreement` (list entry) into a shell template — the
   *  list endpoint already carries enough for the dropdown label and
   *  metadata chips. `content` stays empty until the detail fetch fills
   *  it in. */
  private toShellTemplate(agreement: IAgreement): TermTemplate {
    return {
      id: agreement.id,
      name: this.labelFor(agreement),
      nameByLocale: this.toLocaleBag(agreement.name),
      content: {},
      agreementType: agreement.agreementType,
      status: agreement.status,
      initialDate: agreement.initialDate,
      specName:
        extractMultilingual(agreement.agreementSpecification?.name, "en-US") ||
        extractMultilingual(agreement.agreementSpecification?.name, "th-TH") ||
        undefined,
      specId: agreement.agreementSpecification?.id,
    };
  }

  /** Build the per-locale HTML body from a fully-hydrated agreement.
   *  The AIS BE puts the clause body directly on `description` (a
   *  LocaleString) — the TMF `agreementItem[]` field is unused in
   *  practice. We treat each locale's `description` value as opaque
   *  HTML/text (the preview renders via `[innerHTML]`). */
  private buildContent(agreement: IAgreement): Record<string, string> {
    const content: Record<string, string> = {};
    for (const locale of SUPPORTED_LOCALES) {
      const text = extractMultilingual(agreement.description, locale);
      if (text) content[locale] = text;
    }
    return content;
  }

  private labelFor(agreement: IAgreement): string {
    return (
      extractMultilingual(agreement.name, "en-US") ||
      extractMultilingual(agreement.name, "th-TH") ||
      agreement.id
    );
  }

  /** Convert an optional LocaleString to a plain string-keyed bag for
   *  the FE (`undefined` values become absent keys). */
  private toLocaleBag(
    value: Partial<Record<SupportedLocale, string>> | undefined,
  ): Record<string, string> {
    const bag: Record<string, string> = {};
    if (!value) return bag;
    for (const locale of SUPPORTED_LOCALES) {
      const v = value[locale];
      if (typeof v === "string" && v.length > 0) bag[locale] = v;
    }
    return bag;
  }
}
